import { AlertTriangle, RefreshCw } from 'lucide-react'
import posthog from 'posthog-js'
import { Component, type ErrorInfo, type ReactNode } from 'react'

import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import {
  CHUNK_LOAD_RETRY_KEY,
  saveChunkLoadRecovery,
} from '@/lib/chunkLoadRecovery'

const MAX_RETRIES = 3

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface State {
  hasError: boolean
  error?: Error
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
  public readonly state: State = {
    hasError: false,
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)

    if (error.message.includes('Failed to fetch dynamically imported module')) {
      const attempts = Number.parseInt(
        sessionStorage.getItem(CHUNK_LOAD_RETRY_KEY) ?? '0',
        10
      )

      if (attempts < MAX_RETRIES) {
        const nextAttempt = attempts + 1
        sessionStorage.setItem(CHUNK_LOAD_RETRY_KEY, String(nextAttempt))
        saveChunkLoadRecovery(nextAttempt)
        window.location.reload()
        return
      }

      sessionStorage.removeItem(CHUNK_LOAD_RETRY_KEY)
    }

    posthog.captureException({
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    })

    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }
  }

  private readonly handleReset = () => {
    this.setState({ hasError: false, error: undefined })
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <Card className="mx-auto mt-8 max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive-text">
              <AlertTriangle className="h-5 w-5" />
              Something went wrong
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground text-sm">
              An unexpected error occurred. Please try refreshing the page or
              contact support if the problem persists.
            </p>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="rounded bg-muted p-2 text-xs">
                <summary className="cursor-pointer font-medium">
                  Error details
                </summary>
                <pre className="mt-2 whitespace-pre-wrap">
                  {this.state.error.stack}
                </pre>
              </details>
            )}
            <div className="flex gap-2">
              <Button
                onClick={this.handleReset}
                variant="outline"
                size="sm"
                className="flex-1"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
              <Button
                onClick={() => window.location.reload()}
                size="sm"
                className="flex-1"
              >
                Refresh Page
              </Button>
            </div>
          </CardContent>
        </Card>
      )
    }

    return this.props.children
  }
}
